import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  WritableSignal,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BlotterGridStateKey, GenericLeg } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import {
  AgGridService,
  BlotterRow,
  BlotterService,
  BlotterState,
  ConfigService,
  EntityMapperService,
  ProductsConfigService,
  RfqService,
  UserRole,
  UserStorageService
} from '@approot/services';
import { legsToRfqBuilderLegs } from '@approot/services/rfq/rfq-tab/rfq-tab';
import { DateUtilities, reformatDate } from '@approot/utilities';
import { lowerCaseProductType } from '@approot/widgets/strategy-detail/leg/utilities/leg.utilities';
import {
  AgGridEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import dayjs from 'dayjs';
import { clone } from 'lodash';
import { Throttle } from 'lodash-decorators';
import { applyColumnState } from './blotter.funcs';
@Component({
  selector: 'app-blotter',
  templateUrl: './blotter.component.html',
  styleUrls: ['./blotter.component.scss']
})
export class BlotterComponent implements OnInit {
  @Input() public loginState: boolean;
  @Output() public tradesLoaded = new EventEmitter<GenericLeg[]>();
  @Output() public getRfqTimeline = new EventEmitter<number>();
  public columnDefs: Header[] = [];
  public blotterForm: FormGroup;
  public selectedTrades: BlotterRow[] = [];
  public sideBarDef: SideBarDef;
  public currentUserRole: WritableSignal<UserRole>;
  public blotterState: WritableSignal<BlotterState>;
  private gridApi: GridApi<BlotterRow>;
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);
  private blotterService = inject(BlotterService);
  private productsConfigService = inject(ProductsConfigService);
  private agGridService = inject(AgGridService);
  private rfqService = inject(RfqService);
  private entityMapper = inject(EntityMapperService);

  public ngOnInit(): void {
    this.blotterState = this.blotterService.blotterState;
    this.currentUserRole = this.rfqService.currentUserRole;
    this.sideBarDef = this.agGridService.sideBarDef;
    this.blotterForm = new FormGroup({
      fromDate: new FormControl(this.blotterState().fromDate, [
        Validators.required
      ])
    });

    const headers = this.configService
      .userSettings()
      .ag_grid_config.find(x => x.identifier === 'rfqBlotterHeaders')
      ?.headers as string[];

    if (headers) {
      this.columnDefs = this.agGridService.headers.filter(header =>
        headers.includes(header.field)
      );
    }
  }

  @Throttle(1_000)
  public get lastUpdatedAtStr(): string {
    return dayjs(this.blotterState().lastUpdatedAt).fromNow();
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    this.loadUserStorage(params);

    if (!this.blotterState().lastUpdatedAt) {
      this.updateBlotterData();
    }
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(BlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(BlotterGridStateKey);
    }
  }

  public onDateChange(): void {
    const formDateCtrl = this.blotterForm.controls['fromDate'];
    setTimeout(() => {
      if (DateUtilities.isDateOfSlashFormat(formDateCtrl.value)) {
        formDateCtrl.setValue(reformatDate(formDateCtrl.value, 'D MMM YYYY'));
      }
    }, 0);
  }

  public updateBlotterData(): void {
    const formDateCtrl = this.blotterForm.controls['fromDate'];
    this.blotterState.update(state => {
      const clonedState = clone(state);
      clonedState.fromDate = formDateCtrl.value;
      return clonedState;
    });
    this.gridApi.showLoadingOverlay();
    this.selectedTrades = [];
    this.blotterService
      .getRfq(reformatDate(formDateCtrl.value, 'YYYY-MM-DD'))
      .subscribe({
        next: rows => {
          rows = rows.map(row => {
            row.accountName = this.entityMapper.getDisplayEntity(
              row.accountName
            );
            return row;
          });
          this.blotterState.update(state => {
            const clonedState = clone(state);
            clonedState.rows = rows;
            clonedState.lastUpdatedAt = new Date();
            return clonedState;
          });
        },
        error: _ => this.gridApi.hideOverlay()
      });
  }

  public openTrades(): void {
    this.selectedTrades.forEach(row => {
      const preRfqLegs = row.data.hydra.legs.map(leg => {
        const product =
          this.productsConfigService.productInfo.productSpecifications.find(
            product =>
              lowerCaseProductType(product.productType.longName) ===
              leg.productType
          );
        return {
          productType: product.productType.shortName,
          product: leg
        };
      });

      this.rfqService.addNewTab(
        legsToRfqBuilderLegs(preRfqLegs),
        null,
        {
          hedges: row.data.hydra.hedges,
          setup: row.data.hydra.setup
        },
        false
      );
    });
  }

  public onSelectionChanged(): void {
    this.selectedTrades = this.gridApi.getSelectedRows();
  }

  private loadUserStorage(params: GridReadyEvent): void {
    const columnState = this.userStorageService.getItem(BlotterGridStateKey);
    if (columnState) {
      applyColumnState(params.columnApi, columnState);
    }

    const clonedColumnDefs = clone(this.columnDefs);
    clonedColumnDefs[0] = {
      ...clonedColumnDefs[0],
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true
    };

    this.gridApi.setColumnDefs(clonedColumnDefs as ColDef[]);
    this.gridApi.refreshCells();
  }

  public requestRfqTimeline(requestId: number): void {
    this.getRfqTimeline.emit(requestId);
  }
}
